import {api} from './api';
import {Additional, SubscriptionStatus, SubscriptionType} from '../../features/auth/types';
import {buildURLSearch} from '../utils/apiUtils';
import type {Invoice} from '../types';

export const subscriptionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    buyAdditionals: builder.mutation<BuyAdditionalsResultType, BuyAdditionalsQueryArg>({
      query: (arg) => ({
        url: `/subscriptions/active/additionals${buildURLSearch(arg)}`,
        method: 'POST',
      }),
      invalidatesTags: ['Invoices'],
    }),
    cancelSubscription: builder.mutation<CancelSubscriptionResultType, CancelSubscriptionQueryArg>({
      query: () => ({
        url: `/subscriptions/active`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invoices'],
    }),
    getAdditionals: builder.query<GetAdditionalsResultType, GetAdditionalsQueryArg>({
      query: () => `/additionals`,
    }),
    getInvoices: builder.query<GetInvoicesResultType, GetInvoicesQueryArg>({
      query: () => `/invoices`,
      providesTags: ['Invoices'],
    }),
    getSubscriptionTypes: builder.query<GetSubscriptionTypesResultType, GetSubscriptionTypesQueryArg>({
      query: () => `/subscription-types`,
    }),
    getSubscriptionStatus: builder.query<GetSubscriptionStatusResultType, GetSubscriptionStatusQueryArg>({
      query: () => `/subscription-status`,
    }),
    reactivateSubscription: builder.mutation<ReactivateSubscriptionResultType, ReactivateSubscriptionQueryArg>({
      query: () => ({
        url: `/subscriptions/active/status`,
        method: 'DELETE',
      }),
    }),
    subscribe: builder.mutation<SubscribeResultType, SubscribeQueryArg>({
      query: (arg) => ({url: `/subscriptions${buildURLSearch(arg)}`, method: 'POST'}),
    }),
    switchToYearlySubscription: builder.mutation<
      SwitchToYearlySubscriptionResultType,
      SwitchToYearlySubscriptionQueryArg
    >({
      query: () => ({url: `/subscriptions/active/period`, method: 'PATCH'}),
      invalidatesTags: ['Invoices'],
    }),
    updateBillingDetails: builder.mutation<UpdateBillingDetailsResultType, UpdateBillingDetailsQueryArg>({
      query: () => ({
        url: `/billing-details`,
        method: 'PATCH',
      }),
    }),
    upgradeSubscription: builder.mutation<UpgradeSubscriptionResultType, UpgradeSubscriptionQueryArg>({
      query: (arg) => ({
        url: `/subscriptions/active${buildURLSearch(arg)}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Invoices'],
    }),
  }),
});

export const {
  useBuyAdditionalsMutation,
  useCancelSubscriptionMutation,
  useGetAdditionalsQuery,
  useGetInvoicesQuery,
  useGetSubscriptionTypesQuery,
  useLazyGetSubscriptionStatusQuery,
  useReactivateSubscriptionMutation,
  useSubscribeMutation,
  useSwitchToYearlySubscriptionMutation,
  useUpdateBillingDetailsMutation,
  useUpgradeSubscriptionMutation,
} = subscriptionApi;

export type BuyAdditionalsResultType = void;

export interface BuyAdditionalsQueryArg {
  additionalIds: number[];
}

export type CancelSubscriptionResultType = void;

export type CancelSubscriptionQueryArg = void;

export type GetAdditionalsResultType = Additional[];

export type GetAdditionalsQueryArg = void;

export type GetInvoicesResultType = Invoice[];

export type GetInvoicesQueryArg = void;

export type GetSubscriptionStatusResultType = SubscriptionStatus;

export type GetSubscriptionStatusQueryArg = void;

export type GetSubscriptionTypesResultType = SubscriptionType[];

export type GetSubscriptionTypesQueryArg = void;

export type ReactivateSubscriptionResultType = void;

export type ReactivateSubscriptionQueryArg = void;

export type SubscribeResultType = null | {url: string};

export interface SubscribeQueryArg {
  additionalIds?: number[];
  promotionCode?: string;
  subscriptionTypeId?: number;
  successUrl?: string;
}

export type SwitchToYearlySubscriptionResultType = void;

export type SwitchToYearlySubscriptionQueryArg = void;

export type UpdateBillingDetailsResultType = {url: string};

export type UpdateBillingDetailsQueryArg = void;

export type UpgradeSubscriptionResultType = {url: string};

export type UpgradeSubscriptionQueryArg = {
  subscriptionTypeId: number;
  successUrl?: string;
};
